import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Maximize2, RotateCcw } from "lucide-react";
import { SLIDES, TOTAL } from "./pitch/slides";
import "./pitch/deck.css";
import "./gtm/gtm.css";

const STAGE_W = 960;
const STAGE_H = 540;

export default function InvestorPitchV4() {
  const [i, setI] = useState(() => {
    const h = parseInt((window.location.hash || "").replace("#", ""), 10);
    return Number.isFinite(h) && h >= 1 && h <= TOTAL ? h - 1 : 0;
  });
  const [scale, setScale] = useState(1);
  const [portrait, setPortrait] = useState(false);
  const touch = useRef(null);

  useLayoutEffect(() => {
    const fit = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const reserve = vh < 620 ? 110 : 0;
      setScale(Math.min(vw / STAGE_W, (vh - reserve) / STAGE_H));
      setPortrait(vh > vw && vw < 820);
    };
    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, []);

  const go = useCallback((n) => {
    const next = Math.max(0, Math.min(TOTAL - 1, n));
    setI(next);
    window.history.replaceState(null, "", `#${next + 1}`);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { e.preventDefault(); go(i + 1); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); go(i - 1); }
      else if (e.key === "Home") go(0);
      else if (e.key === "End") go(TOTAL - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [i, go]);

  useEffect(() => {
    const onHash = () => {
      const h = parseInt((window.location.hash || "").replace("#", ""), 10);
      if (Number.isFinite(h) && h >= 1 && h <= TOTAL) setI(h - 1);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? i + 1 : i - 1);
    touch.current = null;
  };

  const fullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const Slide = SLIDES[i];

  return (
    <div
      className="vpd-root"
      data-testid="pitch-deck-root"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="vpd-progress" style={{ width: `${((i + 1) / TOTAL) * 100}%` }} data-testid="pitch-progress" />

      <div className="vpd-viewport">
        <div className="vpd-stage" style={{ transform: `scale(${scale})` }} data-testid="pitch-stage">
          <Slide key={i} />
        </div>
      </div>

      <div className="vpd-ui vpd-topbar">
        <a
          className="vpd-btn"
          href="/downloads/verqo-investor-pitch.pdf"
          download
          data-testid="pitch-download-pdf"
        >
          <Download size={13} /> PDF
        </a>
        <button className="vpd-btn" onClick={fullscreen} data-testid="pitch-fullscreen" aria-label="Full screen">
          <Maximize2 size={13} /> Full
        </button>
      </div>

      <div className="vpd-ui vpd-nav">
        <button className="vpd-arrow" onClick={() => go(i - 1)} disabled={i === 0} data-testid="pitch-prev" aria-label="Previous slide">
          <ChevronLeft size={17} />
        </button>
        <div className="vpd-dots" data-testid="pitch-dots">
          {SLIDES.map((_, n) => (
            <button
              key={n}
              className={`vpd-dot ${n === i ? "on" : ""}`}
              onClick={() => go(n)}
              data-testid={`pitch-dot-${n + 1}`}
              aria-label={`Slide ${n + 1}`}
            />
          ))}
        </div>
        <span className="vpd-navcount" data-testid="pitch-counter">
          <b>{String(i + 1).padStart(2, "0")}</b> / {TOTAL}
        </span>
        <button className="vpd-arrow" onClick={() => go(i + 1)} disabled={i === TOTAL - 1} data-testid="pitch-next" aria-label="Next slide">
          <ChevronRight size={17} />
        </button>
      </div>

      {portrait && (
        <div className="vpd-rotate" data-testid="pitch-rotate-hint">
          <RotateCcw size={12} /> ROTATE FOR FULL SIZE · SWIPE TO MOVE
        </div>
      )}
    </div>
  );
}
